import { useContext, useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import { ReactEditor, useSlate } from 'slate-react';
import { Transforms } from 'slate';
import { EditorContext, useEditorMode } from 'app/state/contexts/EditorContext';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { DataImage } from 'app/components';
import { useImage } from 'app/api/documents/image-queries';
import { i18n } from 'app/utils/i18n';
import { SidebarContext } from 'app/state/contexts/SidebarContext';
import { TagContext } from 'app/state/contexts/TagContext';
import { useBlockTagColors, blockTagHighlightStyle } from 'app/slate/extensions/tag/utils/useBlockTagColors';
import { BlockTagBadge } from 'app/slate/extensions/tag/components/BlockTagBadge';
import { DeleteBlockModal } from './DeleteBlockModal';

const ImagePlaceholder = (imageMetaData) => {
  const ratio = parseFloat(793 / (imageMetaData.width ?? 793)).toFixed(2);
  return (
    <div
      style={{
        width: '793px',
        height: `${Math.round(ratio * (imageMetaData.height ?? 100))}px`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {i18n('imageimport.loading')}
    </div>
  );
};

export const DocumentImage = (props) => {
  const editor = useSlate();
  const { imageSuperId, style, uuid: elementUuid, tags } = props.element;
  const image = useEditorSelector((editor) => editor.pDocument.content.images?.find?.((image) => image.superId === imageSuperId));
  const { data: imageData, isLoading: isImageLoading } = useImage(image?.revisionId, 'screen');
  const { setBlockSelection, clearBlockSelection, setClickedTagIds, blockSelection } = useContext(TagContext) ?? {};
  const { setIsSidebarOpen } = useContext(SidebarContext) ?? {};
  const { shadowHost } = useContext(EditorContext) || {};
  const { isTaggingMode, taggingSubMode } = useEditorMode();
  const containerRef = useRef(null);
  const composedRef = useCallback(
    (node) => {
      containerRef.current = node;
      const slateRef = props.attributes?.ref;
      if (typeof slateRef === 'function') slateRef(node);
      else if (slateRef && typeof slateRef === 'object') slateRef.current = node;
    },
    [props.attributes]
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const performDelete = () => {
    const path = ReactEditor.findPath(editor, props?.element);
    Transforms.select(editor, path);
    Transforms.removeNodes(editor, { at: path });
  };
  const confirmDelete = () => {
    setShowDeleteModal(false);
    performDelete();
  };

  const blockTagBindings = useMemo(() => (Array.isArray(tags) ? tags : []), [tags]);
  const hasBlockTag = blockTagBindings.length > 0;

  const isEmbedded = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('embedd') === 'true';
  }, []);
  const documentPermissions = useSelector((state) => state.document?.documentPermissions) || [];
  const canEditTags = documentPermissions.includes('TAG_EDITOR') || documentPermissions.includes('TAG_ADMIN');
  const onImageClick = useCallback(
    (e) => {
      if (props.readOnly) return;
      if (e.target.closest && e.target.closest('.controls--container')) return;
      // Coverage mode: images are read-only (not a coverage target, no tag editing).
      if (taggingSubMode === 'mark-coverage') return;
      if (hasBlockTag) {
        setClickedTagIds?.(blockTagBindings.map((b) => b.tagId));
        setIsSidebarOpen?.(8);
        return;
      }
      try {
        const path = ReactEditor.findPath(editor, props.element);
        let uuid = elementUuid;
        if (!uuid) {
          uuid = uuidv4();
          Transforms.setNodes(editor, { uuid }, { at: path });
        }
        setBlockSelection?.({ kind: 'image', path, elementId: uuid });
        setIsSidebarOpen?.(8);
      } catch (err) {
        console.warn(`Path lookup failed`);
      }
    },
    [props.readOnly, props.element, hasBlockTag, blockTagBindings, setBlockSelection, setClickedTagIds, setIsSidebarOpen, editor, elementUuid, taggingSubMode]
  );

  const isBlockSelected = !!elementUuid && blockSelection?.kind === 'image' && blockSelection.elementId === elementUuid;

  useEffect(() => {
    if (!shadowHost?.shadowRoot || !isBlockSelected) return;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        clearBlockSelection?.();
      }
    };
    shadowHost.shadowRoot.addEventListener('mousedown', handleClickOutside);
    return () => {
      shadowHost.shadowRoot.removeEventListener('mousedown', handleClickOutside);
    };
  }, [shadowHost, isBlockSelected, clearBlockSelection]);
  const blockTagClass = isBlockSelected ? ' editor-image--container--block-tagged' : '';

  const blockTagColors = useBlockTagColors(hasBlockTag ? blockTagBindings : undefined);
  const showBlockTagVisual = hasBlockTag && !isEmbedded && isTaggingMode && blockTagColors && !blockTagColors.isHidden;
  const blockTagStyle = {
    ...(showBlockTagVisual ? blockTagHighlightStyle(blockTagColors) : {}),
    ...(isBlockSelected && blockTagColors ? { outlineColor: blockTagColors.indicator } : {}),
  };

  return (
    <div
      {...props.attributes}
      ref={composedRef}
      className={`editor-image--container${blockTagClass}`}
      style={{ ...style, ...blockTagStyle }}
      data-element-uuid={elementUuid}
      onClick={onImageClick}
      contentEditable={false}
    >
      {!props.readOnly && (
        <div className="controls--container">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteModal(true);
            }}
          >
            <i className="fa-regular fa-trash-can" style={{ fontFamily: 'Font Awesome 6 Pro' }} />
          </button>
        </div>
      )}
      <DeleteBlockModal
        show={showDeleteModal}
        message={i18n('block.delete.modal.image')}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
      {hasBlockTag && !isEmbedded && isTaggingMode && (
        <BlockTagBadge
          bindings={blockTagBindings}
          tooltipKey="tag.block.image.tagged"
          canEdit={canEditTags}
          onContinueClick={(firstTagId) => {
            setClickedTagIds?.([firstTagId]);
            setIsSidebarOpen?.(8);
          }}
          onSelectTag={(tagId) => {
            setClickedTagIds?.([tagId]);
            setIsSidebarOpen?.(8);
          }}
        />
      )}
      {isImageLoading && <ImagePlaceholder {...imageData?.metadata} />}
      {imageData ? <DataImage {...imageData.file} /> : !isImageLoading ? <i className="fa-regular fa-image" /> : null}
      {props.children}
    </div>
  );
};
